/**
 * A one-line banner at the top of the content, on every viewport, shown only
 * when sync needs the user (reconnect, grant Drive access, sign in, failure).
 * On phones there is no sidebar chip, so this is where those states surface.
 * Dismissing hides it until the status changes.
 */
import { useState } from 'react'
import { KeyRound, AlertTriangle, LogIn } from 'lucide-react'
import { useI18n } from '../../lib/i18n/i18n-store.ts'
import { ACTIONABLE, useSyncActions } from './useSyncActions.ts'
import { useAuth } from '../../hooks/useAuth.tsx'
import './SyncAttentionBanner.css'

export function SyncAttentionBanner() {
  const { t } = useI18n()
  const { status, primary } = useSyncActions()
  const { hasAccount } = useAuth()
  const [dismissedFor, setDismissedFor] = useState<string | null>(null)

  if (!ACTIONABLE.has(status) || !primary || dismissedFor === status) return null
  // A device that never signed in sees the sign-in screen instead.
  if (status === 'signed_out' && !hasAccount) return null

  const Icon = status === 'signed_out' ? LogIn : status === 'error' ? AlertTriangle : KeyRound
  return (
    <div className={`syncban syncban--${status}`} role="status">
      <Icon size={15} className="syncban-icon" aria-hidden="true" />
      <p className="syncban-text">
        <strong>{t(`sync.status.${status}`)}.</strong> {t(`sync.detail.${status}`)}
      </p>
      <button className="btn btn--primary btn--sm" onClick={primary.run}>{primary.label}</button>
      <button className="btn btn--ghost btn--sm" onClick={() => setDismissedFor(status)}>{t('common.dismiss')}</button>
    </div>
  )
}
